// recovery-manager.service.ts
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import {
  AppStateService,
  CACHE_INIT_KEY,
  KEY_APP_STATE,
  TokenService
} from 'viggo-sistemas-core-web';
import {
  KEY_RECOVERY_ATTEMPS,
  KEY_RECOVERY_HISTORY,
  KEY_RECOVERY_LAST_RECOVERY_TIME
} from '../constants';

export interface RecoveryInfo {
  reason: string;
  details: string;
  startTime: Date;
  estimatedDuration: number; // em segundos
  progress?: number; // 0-100
  steps?: string[];
  currentStep?: number;
}

@Injectable({
  providedIn: 'root'
})
export class RecoveryManagerService {
  private recoveryInProgress = new BehaviorSubject<boolean>(false);
  private recoveryInfo = new BehaviorSubject<RecoveryInfo | null>(null);
  private recoveryHistory: RecoveryInfo[] = [];
  private cooldownPeriodMs = 30000; // 30 segundos de cooldown entre recuperações
  private lastRecoveryTime = 0;
  private recoveryAttempts = 0;
  private maxRecoveryAttempts = 2; // máximo de tentativas em sequência
  private recoveryInCooldown = false;

  // Expondo observables
  public recoveryInProgress$ = this.recoveryInProgress.asObservable();
  public recoveryInfo$ = this.recoveryInfo.asObservable();

  constructor(
    private matDialog: MatDialog,
    private router: Router,
    protected tokenService: TokenService,
    protected appState: AppStateService
  ) {
    this.loadRecoveryState();
  }

  /**
   * Carrega o estado de recuperação salvo
   */
  private loadRecoveryState(): void {
    try {
      const history = localStorage.getItem(KEY_RECOVERY_HISTORY);
      if (history) {
        this.recoveryHistory = JSON.parse(history);
      }

      const lastTime = localStorage.getItem(KEY_RECOVERY_LAST_RECOVERY_TIME);
      if (lastTime) {
        this.lastRecoveryTime = parseInt(lastTime, 10);
      }

      const attempts = localStorage.getItem(KEY_RECOVERY_ATTEMPS);
      if (attempts) {
        this.recoveryAttempts = parseInt(attempts, 10);
      }

      this.checkAndResetRecoveryAttempts();
    } catch (e) {
      console.error('Erro ao carregar estado de recuperação:', e);
    }
  }

  /**
   * Inicia o processo de recuperação visual
   * @param reason Motivo da recuperação
   * @param details Detalhes adicionais
   * @param steps Lista de etapas do processo (opcional)
   * @returns Promise que resolve quando o processo estiver concluído
   */
  async startRecovery(
    reason: string,
    details: string = '',
    steps?: string[]
  ): Promise<void> {
    // Já há um processo em andamento
    if (this.recoveryInProgress.value) {
      console.log('Recuperação já está em andamento');
      return;
    }

    // Verificar se estamos em período de cooldown
    const now = Date.now();
    if (
      this.recoveryInCooldown ||
      now - this.lastRecoveryTime < this.cooldownPeriodMs
    ) {
      console.log('Recuperação em cooldown, ignorando tentativa');
      return;
    }

    // Verificar número de tentativas recentes
    if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
      // console.warn(
      //   `Máximo de ${this.maxRecoveryAttempts} tentativas de recuperação atingido. Entrando em modo seguro.`
      // );
      this.enterSafeMode();
      return;
    }

    // Incrementar contador de tentativas
    this.recoveryAttempts++;
    localStorage.setItem(
      KEY_RECOVERY_ATTEMPS,
      this.recoveryAttempts.toString()
    );

    const info: RecoveryInfo = {
      reason,
      details,
      startTime: new Date(),
      estimatedDuration: 10, // padrão: 10 segundos
      progress: 0,
      steps,
      currentStep: 0
    };

    // Atualizar estado
    this.recoveryInProgress.next(true);
    this.recoveryInfo.next(info);

    console.log('Processo de recuperação visual iniciado:', reason);
  }

  /**
   * Atualiza o progresso da recuperação
   * @param progress Percentual de progresso (0-100)
   * @param step Etapa atual (opcional)
   */
  updateProgress(progress: number, step?: number): void {
    const info = this.recoveryInfo.value;
    if (info) {
      info.progress = progress;
      if (step !== undefined) {
        info.currentStep = step;
      }
      this.recoveryInfo.next(info);
    }
  }

  /**
   * Finaliza o processo de recuperação
   * @param success Se a recuperação foi bem-sucedida
   */
  async finishRecovery(success: boolean = true): Promise<void> {
    const info = this.recoveryInfo.value;

    // Registrar timestamp da última recuperação
    this.lastRecoveryTime = Date.now();
    localStorage.setItem(
      'last-recovery-time',
      this.lastRecoveryTime.toString()
    );

    // Iniciar período de cooldown
    this.recoveryInCooldown = true;
    setTimeout(() => {
      this.recoveryInCooldown = false;
    }, this.cooldownPeriodMs);

    if (info) {
      // Registrar no histórico
      this.recoveryHistory.push({ ...info });

      // Limitar o histórico a 10 entradas
      if (this.recoveryHistory.length > 10) {
        this.recoveryHistory = this.recoveryHistory.slice(-10);
      }

      // Salvar histórico
      try {
        localStorage.setItem(
          'recovery-history',
          JSON.stringify(this.recoveryHistory)
        );
      } catch (e) {
        console.error('Erro ao salvar histórico de recuperações:', e);
      }
    }

    // Resetar estado
    this.recoveryInProgress.next(false);
    this.recoveryInfo.next(null);

    // Se a recuperação foi bem-sucedida, resetar o contador de tentativas
    if (success) {
      this.matDialog.closeAll();
      window.location.reload();
      this.recoveryAttempts = 0;
      localStorage.setItem(KEY_RECOVERY_ATTEMPS, '0');
      // Aguardar um momento antes de redirecionar para garantir que o estado foi atualizado
      await new Promise(resolve => setTimeout(resolve, 500));

      // Usar query params para evitar loops (esta página não deve iniciar recuperação)
      await this.router.navigate(['/'], {
        queryParams: { noRecovery: 'true' },
        replaceUrl: true
      });
    } else {
      // Em caso de falha, tentar modo seguro se muitas tentativas
      if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
        this.enterSafeMode();
      } else {
        // Caso contrário, voltar para a página inicial
        this.matDialog.closeAll();
        window.location.reload();
        await new Promise(resolve => setTimeout(resolve, 500));
        await this.router.navigate(['/'], {
          queryParams: { noRecovery: 'true' },
          replaceUrl: true
        });
      }
    }
  }

  /**
   * Entra em modo seguro após múltiplas falhas de recuperação
   */
  private enterSafeMode(): void {
    console.log(
      'Entrando em modo seguro após múltiplas tentativas de recuperação'
    );

    // Limpar todos os dados e caches
    try {
      localStorage.clear();
      sessionStorage.clear();
      caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });

      // Salvar flag de modo seguro
      localStorage.setItem('safe-mode', 'true');
      localStorage.setItem('safe-mode-time', Date.now().toString());
    } catch (e) {
      console.error('Erro ao entrar em modo seguro:', e);
    }

    // Redirecionar para página especial de modo seguro
    this.router.navigate(['/safe-mode'], { replaceUrl: true });
  }

  /**
   * Verifica e reseta o contador de tentativas se necessário
   */
  private checkAndResetRecoveryAttempts(): void {
    const lastRecoveryTime = this.lastRecoveryTime;
    const now = Date.now();

    // Se passou mais de 5 minutos desde a última recuperação, resetar contador
    if (now - lastRecoveryTime > 5 * 60 * 1000) {
      this.recoveryAttempts = 0;
      localStorage.setItem(KEY_RECOVERY_ATTEMPS, '0');
    }
  }

  /**
   * Verifica se deve iniciar recuperação baseado no estado atual da aplicação
   */
  shouldInitiateRecovery(reason: string): boolean {
    // Se estiver sem internet não é pra recuperar
    if (!navigator.onLine) {
      return false;
    }

    // caso o cache esteja inicializando ou tenha inicializado recentemente, não iniciar recuperação
    if (
      (this.appState &&
        localStorage.getItem(CACHE_INIT_KEY) &&
        this.appState.isCacheInitializing()) ||
      this.appState.wasCacheInitializedRecently(10000)
    ) {
      return false;
    }

    // Caso não esteja logado não deve entrar em recuperação
    if (
      this.appState &&
      localStorage.getItem(KEY_APP_STATE) &&
      (!this.appState.isAppStable() || !this.appState.currentState)
    ) {
      return false;
    }

    // Verificar se estamos em cooldown
    if (this.recoveryInCooldown) {
      return false;
    }

    // Verificar se já estamos em processo de recuperação
    if (this.recoveryInProgress.value) {
      return false;
    }

    // Verificar se há parâmetro na URL indicando que acabamos de recuperar
    if (window.location.search.includes('noRecovery=true')) {
      return false;
    }

    // Verificar número de tentativas
    if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
      this.enterSafeMode();
      return false;
    }

    // Verificar tempo desde a última recuperação
    const timeSinceLastRecovery = Date.now() - this.lastRecoveryTime;
    if (timeSinceLastRecovery < this.cooldownPeriodMs) {
      return false;
    }

    return true;
  }

  /**
   * Obtém o histórico de recuperações
   */
  getRecoveryHistory(): RecoveryInfo[] {
    return [...this.recoveryHistory];
  }

  /**
   * Obtém a última recuperação
   */
  getLastRecovery(): RecoveryInfo | null {
    if (this.recoveryHistory.length > 0) {
      return this.recoveryHistory[this.recoveryHistory.length - 1];
    }
    return null;
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: Date): string {
    return date.toLocaleString();
  }
}
